import React, { useEffect, useState } from 'react';
import { Redirect as RedirectBase, Route as RouteBase } from 'react-router-dom';

const Route = RouteBase as any;
const Redirect = RedirectBase as any;
import {
  IonApp,
  IonRouterOutlet,
  setupIonicReact,
  IonSplitPane,
  IonMenu,
  IonContent,
  IonList,
  IonItem,
  IonIcon,
  IonLabel,
  IonMenuToggle,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonFooter,
  IonButtons,
  IonButton,
  IonSpinner,
  IonPage,
  IonMenuButton,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import {
  homeOutline,
  ticketOutline,
  cartOutline,
  statsChartOutline,
  analyticsOutline,
  personOutline,
  logOutOutline,
  settingsOutline,
  moonOutline,
  sunnyOutline,
  languageOutline,
  menuOutline,
  calendarOutline,
  mailOutline,
  businessOutline,
  cloudUploadOutline,
  cloudDownloadOutline,
  shieldCheckmarkOutline,
  chatbubbleEllipsesOutline,
  chatbubblesOutline,
  logoWhatsapp,
  listOutline,
  cubeOutline,
  cardOutline,
  pricetagsOutline,
  barChartOutline,
  trendingUpOutline,
} from 'ionicons/icons';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventProvider } from './contexts/EventContext';
import ManageTixLogo from './components/ManageTixLogo';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Sales from './pages/Sales';
import Orders from './pages/Orders';
import Reports from './pages/Reports';
import Events from './pages/Events';
import EventDetails from './pages/EventDetails';
import GoogleCallback from './pages/GoogleCallback';
import Settings from './pages/Settings';
import EmailConfiguration from './pages/EmailConfiguration';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserDetails from './pages/AdminUserDetails';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Suppliers from './pages/Suppliers';
import Import from './pages/Import';
import Export from './pages/Export';
import Chat from './pages/Chat';
import WhatsApp from './pages/WhatsApp';
import Listings from './pages/Listings';
import MarketData from './pages/MarketData';
import Stock from './pages/Stock';
import Expenses from './pages/Expenses';
import Summary from './pages/Summary';
import EventSales from './pages/EventSales';
import Messages from './pages/Messages';
import OnboardingTrigger from './components/OnboardingTrigger';
import OnboardingButton from './components/OnboardingButton';
import './i18n/i18n';
import { useTranslation } from 'react-i18next';

/* Theme variables */
import './theme/variables.css';

setupIonicReact();

interface AppPage {
  url: string;
  icon: string;
  title: string;
}

const appPages: AppPage[] = [
  { title: 'dashboard.title', url: '/dashboard', icon: homeOutline },
  { title: 'Orders', url: '/orders', icon: listOutline },
  { title: 'Stock', url: '/stock', icon: cubeOutline },
  { title: 'Expenses', url: '/expenses', icon: cardOutline },
  { title: 'Summary', url: '/summary', icon: analyticsOutline },
  { title: 'Messages', url: '/messages', icon: chatbubblesOutline },
  { title: 'Listings', url: '/listings', icon: pricetagsOutline },
  { title: 'Market Data', url: '/market-data', icon: barChartOutline },
  { title: 'Sales by Event', url: '/event-sales', icon: trendingUpOutline },
  { title: 'events.title', url: '/events', icon: calendarOutline },
  { title: 'AI Assistant', url: '/chat', icon: chatbubbleEllipsesOutline },
  { title: 'WhatsApp AI', url: '/whatsapp', icon: logoWhatsapp },
  { title: 'Suppliers', url: '/suppliers', icon: businessOutline },
  { title: 'Import', url: '/import', icon: cloudUploadOutline },
  { title: 'Export', url: '/export', icon: cloudDownloadOutline },
  { title: 'reports.title', url: '/reports', icon: statsChartOutline },
  {
    title: 'emailConfig.title',
    url: '/email-configuration',
    icon: mailOutline,
  },
];

const ProtectedRoute: React.FC<{
  component: React.FC<any>;
  path: string;
  exact?: boolean;
}> = ({ component: Component, ...rest }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <IonPage>
        <IonContent className="ion-padding ion-text-center">
          <IonSpinner />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <Route
      {...rest}
      render={(props: any) =>
        user ? <Component {...props} /> : <Redirect to="/login" />
      }
    />
  );
};

const Menu: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    setDarkMode(prefersDark.matches);
    document.body.classList.toggle('dark', prefersDark.matches);

    // Set initial direction based on current language
    const currentLang = i18n.language;
    document.dir = currentLang === 'ar' || currentLang === 'he' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    document.body.classList.toggle('dark', newDarkMode);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    document.dir = lng === 'ar' || lng === 'he' ? 'rtl' : 'ltr';
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <IonMenu
      menuId="main-menu"
      contentId="main-content"
      type="overlay"
      swipeGesture={true}
    >
      <IonHeader>
        <IonToolbar className="bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center justify-center w-full py-2">
            <ManageTixLogo size={32} showText={true} className="text-white" />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="flex flex-col h-full">
          <div className="flex-1">
            <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center">
                  <IonIcon
                    icon={personOutline}
                    className="text-white text-xl"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            <IonList className="p-0">
              {appPages.map((appPage, index) => (
                <IonMenuToggle key={index} autoHide={true}>
                  <IonItem
                    routerLink={appPage.url}
                    routerDirection="none"
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                    lines="inset"
                  >
                    <IonIcon
                      icon={appPage.icon}
                      slot="start"
                      className="text-primary-600 dark:text-primary-400 text-xl"
                    />
                    <IonLabel className="font-medium py-4">
                      {t(appPage.title)}
                    </IonLabel>
                  </IonItem>
                </IonMenuToggle>
              ))}

              {/* Admin-only menu item */}
              {user?.role === 'admin' && (
                <IonMenuToggle autoHide={true}>
                  <IonItem
                    routerLink="/admin"
                    routerDirection="none"
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                    lines="inset"
                  >
                    <IonIcon
                      icon={shieldCheckmarkOutline}
                      slot="start"
                      className="text-red-600 dark:text-red-400 text-xl"
                    />
                    <IonLabel className="font-medium py-4">
                      Admin Panel
                    </IonLabel>
                  </IonItem>
                </IonMenuToggle>
              )}
            </IonList>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('common.theme')}
              </span>
              <IonButton
                fill="clear"
                size="small"
                onClick={toggleDarkMode}
                className="text-primary-600"
              >
                <IonIcon
                  icon={darkMode ? moonOutline : sunnyOutline}
                  slot="icon-only"
                />
              </IonButton>
            </div>

            {/* <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('common.language')}
              </span>
              <IonButton
                fill="clear"
                size="small"
                onClick={() => {
                  const langs = ['en', 'he', 'ar'];
                  const currentIndex = langs.indexOf(i18n.language);
                  const nextIndex = (currentIndex + 1) % langs.length;
                  changeLanguage(langs[nextIndex]);
                }}
                className="text-primary-600"
              >
                <span className="text-sm font-medium">
                  {i18n.language.toUpperCase()}
                </span>
              </IonButton>
            </div> */}

            <OnboardingButton variant="full" fill="outline" className="mb-3" />

            <IonButton
              expand="block"
              fill="solid"
              color="danger"
              onClick={handleLogout}
              className="mt-4"
            >
              <IonIcon icon={logOutOutline} slot="start" />
              {t('auth.logout')}
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonMenu>
  );
};

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <IonPage>
        <IonContent className="ion-padding ion-text-center bg-gradient-to-br from-primary-600 to-purple-700">
          <div className="flex flex-col items-center justify-center h-full space-y-6">
            <ManageTixLogo size={80} showText={true} className="text-white" />
            <IonSpinner name="crescent" className="text-white w-8 h-8" />
            <p className="text-white/80 text-lg">Loading...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonReactRouter>
      {user ? (
        <IonSplitPane contentId="main-content" when={false}>
          <Menu />
          <OnboardingTrigger />
          <IonRouterOutlet id="main-content">
            <ProtectedRoute path="/dashboard" component={Dashboard} exact />
            <ProtectedRoute path="/events" component={Events} exact />
            <ProtectedRoute
              path="/events/:eventId"
              component={EventDetails}
              exact
            />
            <ProtectedRoute path="/chat" component={Chat} exact />
            <ProtectedRoute path="/whatsapp" component={WhatsApp} exact />
            <ProtectedRoute path="/inventory" component={Inventory} exact />
            <ProtectedRoute path="/sales" component={Sales} exact />
            <ProtectedRoute path="/orders" component={Orders} exact />
            <ProtectedRoute path="/stock" component={Stock} exact />
            <ProtectedRoute path="/expenses" component={Expenses} exact />
            <ProtectedRoute path="/summary" component={Summary} exact />
            <ProtectedRoute path="/listings" component={Listings} exact />
            <ProtectedRoute path="/market-data" component={MarketData} exact />
            <ProtectedRoute path="/event-sales" component={EventSales} exact />
            <ProtectedRoute path="/messages" component={Messages} exact />
            <ProtectedRoute path="/reports" component={Reports} exact />
            <ProtectedRoute path="/suppliers" component={Suppliers} exact />
            <ProtectedRoute path="/import" component={Import} exact />
            <ProtectedRoute path="/export" component={Export} exact />
            <ProtectedRoute path="/settings" component={Settings} exact />
            <ProtectedRoute
              path="/email-configuration"
              component={EmailConfiguration}
              exact
            />
            <ProtectedRoute
              path="/google-callback"
              component={GoogleCallback}
              exact
            />
            <ProtectedRoute path="/admin" component={AdminDashboard} exact />
            <ProtectedRoute
              path="/admin/users/:userId"
              component={AdminUserDetails}
              exact
            />
            <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
            <Route render={() => <Redirect to="/dashboard" />} />
          </IonRouterOutlet>
        </IonSplitPane>
      ) : (
        <IonRouterOutlet>
          <Route exact path="/login" component={Login} />
          <Route exact path="/signup" component={Signup} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/privacy" component={Privacy} />
          <Route exact path="/terms" component={Terms} />
          <Route exact path="/" render={() => <Redirect to="/login" />} />
          <Route render={() => <Redirect to="/login" />} />
        </IonRouterOutlet>
      )}
    </IonReactRouter>
  );
};

const App: React.FC = () => (
  <IonApp>
    <AuthProvider>
      <EventProvider>
        <AppContent />
      </EventProvider>
    </AuthProvider>
  </IonApp>
);

export default App;
